import React, { createContext, useContext, useEffect, useState } from 'react';
import API from '../utils/api';

const DEFAULT_BRANDING = { appName: 'Material Stock', supportEmail: 'support@materialstock.in', logoImage: '', darkLogoImage: '', faviconImage: '', darkFaviconImage: '', theme: 'forest' };
const THEME_STORAGE_KEY = 'ms_theme';

const THEMES = {
  graphite: {
    orange: '#334155',
    orangeDark: '#1E293B',
    orangeLight: '#64748B',
    amber: '#F59E0B',
    soft: '#F1F5F9',
    softer: '#F8FAFC',
    border: '#CBD5E1',
    pageBg: '#F8FAFC',
    focus: 'rgba(51, 65, 85, 0.12)',
    shadow: 'rgba(51, 65, 85, 0.18)',
    heroShadow: 'rgba(51, 65, 85, 0.24)'
  },
  forest: {
    orange: '#15803D',
    orangeDark: '#166534',
    orangeLight: '#22C55E',
    amber: '#84CC16',
    soft: '#ECFDF3',
    softer: '#F7FEE7',
    border: '#BBF7D0',
    pageBg: '#F8FCF7',
    focus: 'rgba(21, 128, 61, 0.12)',
    shadow: 'rgba(21, 128, 61, 0.18)',
    heroShadow: 'rgba(21, 128, 61, 0.23)'
  },
  steel: {
    orange: '#2563EB',
    orangeDark: '#1D4ED8',
    orangeLight: '#3B82F6',
    amber: '#06B6D4',
    soft: '#EFF6FF',
    softer: '#F0FDFF',
    border: '#BFDBFE',
    pageBg: '#F7FAFF',
    focus: 'rgba(37, 99, 235, 0.12)',
    shadow: 'rgba(37, 99, 235, 0.18)',
    heroShadow: 'rgba(37, 99, 235, 0.23)'
  },
  brick: {
    orange: '#B45309',
    orangeDark: '#92400E',
    orangeLight: '#D97706',
    amber: '#EA580C',
    soft: '#FFF7ED',
    softer: '#FFFBEB',
    border: '#FED7AA',
    pageBg: '#FBF8F3',
    focus: 'rgba(180, 83, 9, 0.12)',
    shadow: 'rgba(180, 83, 9, 0.18)',
    heroShadow: 'rgba(180, 83, 9, 0.24)'
  },
  indigo: {
    orange: '#4F46E5',
    orangeDark: '#4338CA',
    orangeLight: '#6366F1',
    amber: '#8B5CF6',
    soft: '#EEF2FF',
    softer: '#F5F3FF',
    border: '#C7D2FE',
    pageBg: '#F8F7FF',
    focus: 'rgba(79, 70, 229, 0.12)',
    shadow: 'rgba(79, 70, 229, 0.18)',
    heroShadow: 'rgba(79, 70, 229, 0.23)'
  },
  teal: {
    orange: '#0F766E',
    orangeDark: '#115E59',
    orangeLight: '#14B8A6',
    amber: '#2DD4BF',
    soft: '#ECFEFF',
    softer: '#F0FDFA',
    border: '#99F6E4',
    pageBg: '#F7FCFB',
    focus: 'rgba(15, 118, 110, 0.12)',
    shadow: 'rgba(15, 118, 110, 0.18)',
    heroShadow: 'rgba(15, 118, 110, 0.23)'
  }
};

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  refreshBranding: () => {}
});

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const legacyBrandName = 'Build' + 'Mart';

  const normalizeBrandName = (name) => (name && name !== legacyBrandName ? name : 'Material Stock');
  const normalizeTheme = (theme) => (theme && THEMES[theme] ? theme : 'forest');
  const upsertFavicon = (id, href, media) => {
    if (!href) return;
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
    if (media) link.media = media;
    else link.removeAttribute('media');
  };

  const applyBranding = (nextBranding) => {
    const appName = normalizeBrandName(nextBranding?.appName);
    const selectedTheme = normalizeTheme(nextBranding?.theme);
    const theme = THEMES[selectedTheme] || THEMES.forest;
    const root = document.documentElement;
    root.style.setProperty('--orange', theme.orange);
    root.style.setProperty('--orange-dark', theme.orangeDark);
    root.style.setProperty('--orange-light', theme.orangeLight);
    root.style.setProperty('--amber', theme.amber);
    root.style.setProperty('--orange-soft', theme.soft);
    root.style.setProperty('--orange-softer', theme.softer);
    root.style.setProperty('--orange-border', theme.border);
    root.style.setProperty('--page-bg', theme.pageBg);
    root.style.setProperty('--orange-focus', theme.focus);
    root.style.setProperty('--orange-shadow', theme.shadow);
    root.style.setProperty('--orange-hero-shadow', theme.heroShadow);
    document.title = `${appName} - Construction Materials`;

    const lightFavicon = nextBranding?.faviconImage || nextBranding?.logoImage || '/material-stock-logo.jpg';
    const darkFavicon = nextBranding?.darkFaviconImage || nextBranding?.faviconImage || nextBranding?.darkLogoImage || nextBranding?.logoImage || '/material-stock-logo.jpg';
    upsertFavicon('material-stock-favicon-light', lightFavicon, '(prefers-color-scheme: light)');
    upsertFavicon('material-stock-favicon-dark', darkFavicon, '(prefers-color-scheme: dark)');
    upsertFavicon('material-stock-favicon-default', lightFavicon);
  };

  const refreshBranding = async () => {
    try {
      const res = await API.get('/admin/public-settings');
      const nextBranding = {
        appName: normalizeBrandName(res.data.branding?.appName),
        supportEmail: res.data.branding?.supportEmail || 'support@materialstock.in',
        logoImage: res.data.branding?.logoImage || '',
        darkLogoImage: res.data.branding?.darkLogoImage || '',
        faviconImage: res.data.branding?.faviconImage || '',
        darkFaviconImage: res.data.branding?.darkFaviconImage || '',
        theme: normalizeTheme(res.data.branding?.theme || localStorage.getItem(THEME_STORAGE_KEY))
      };
      localStorage.setItem(THEME_STORAGE_KEY, nextBranding.theme);
      setBranding(nextBranding);
      applyBranding(nextBranding);
    } catch {
      applyBranding(branding);
    }
  };

  useEffect(() => {
    refreshBranding();
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
